import API_BASE from '../config';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { PromptPicker } from '../components/PromptPicker';
import ContactPanel from '../components/ContactPanel';
import FilterChips from '../components/FilterChips';
import FilterRail from '../components/FilterRail';
import { SEQUENCE_SCHEMA } from '../components/filterSchema';
import ConfirmActionModal from '../components/ConfirmActionModal';
import toast from "react-hot-toast";
import { creditAwareFetch, emitCreditsChanged } from '../hooks/creditAwareFetch';

const PAGE_SIZE = 50;

const SEQ_STATUS = {
  none:     { label: 'No sequence', color: '#AAA',    bg: '#F7F7F7' },
  pending:  { label: 'Pending',     color: '#B7791F', bg: '#FFFBEB' },
  approved: { label: 'Approved',    color: '#276749', bg: '#F0FFF4' },
  pushed:   { label: 'Pushed',      color: '#1A56A0', bg: '#EBF8FF' },
};

function seqStatus(seq) {
  if (!seq) return 'none';
  if (seq.pushed_at) return 'pushed';
  if (seq.approval_status === 'approved') return 'approved';
  return 'pending';
}

function StatusBadge({ seq }) {
  const s = SEQ_STATUS[seqStatus(seq)];
  return <span style={{ fontSize: '0.65rem', fontWeight: '600', color: s.color, backgroundColor: s.bg, padding: '0.1rem 0.45rem', borderRadius: '20px', whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function ChannelPill({ channel, status }) {
  const colours = {
    email:    { approved: { c: '#276749', bg: '#F0FFF4' }, pending: { c: '#B7791F', bg: '#FFFBEB' } },
    linkedin: { approved: { c: '#276749', bg: '#F0FFF4' }, pending: { c: '#1A56A0', bg: '#EBF8FF' } },
  };
  const palette = colours[channel]?.[status] || { c: '#888', bg: '#F4F4F4' };
  const label = channel === 'email' ? 'Em' : 'Li';
  return (
    <span style={{ fontSize: '0.6rem', fontWeight: '600', color: palette.c, backgroundColor: palette.bg, padding: '0.1rem 0.4rem', borderRadius: '20px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label} · {status}
    </span>
  );
}

function ChannelBadges({ emailCh, linkedinCh }) {
  const items = [];
  if (emailCh) items.push(<ChannelPill key='e' channel='email' status={emailCh.approval_status} />);
  if (linkedinCh) items.push(<ChannelPill key='l' channel='linkedin' status={linkedinCh.approval_status} />);
  if (items.length === 0) {
    return <span style={{ fontSize: '0.6rem', fontWeight: '600', color: '#AAA', backgroundColor: '#F7F7F7', padding: '0.1rem 0.45rem', borderRadius: '20px', whiteSpace: 'nowrap' }}>No sequence</span>;
  }
  return <span style={{ display: 'inline-flex', gap: '0.3rem' }}>{items}</span>;
}

export default function Sequences() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { getToken } = useAuth();

  const [client, setClient] = useState(null);
  const [allContacts, setAllContacts] = useState([]);
  const [allSequences, setAllSequences] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [addToBatchOpen, setAddToBatchOpen] = useState(false);
  const [addToBatchChannel, setAddToBatchChannel] = useState('linkedin');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenAction, setTokenAction] = useState('insert'); // 'insert' or 'remove'
  const [tokenRows, setTokenRows] = useState({
    cta_url: { enabled: false, strategy: 'end_sales' },
    landing_page: { enabled: false, strategy: 'end_sales' },
    attachment: { enabled: false, strategy: 'end_sales' }
  });
  const [tokenRunning, setTokenRunning] = useState(false);
  const [tokenResult, setTokenResult] = useState(null);
  // LinkedIn token modal — independent state from email tokens
  const [liTokenModalOpen, setLiTokenModalOpen] = useState(false);
  const [liTokenAction, setLiTokenAction] = useState('insert');
  const [liTokenRows, setLiTokenRows] = useState({
    cta_url: { enabled: false, strategy: 'end_sales_li' },
    landing_page: { enabled: false, strategy: 'end_sales_li' },
    attachment: { enabled: false, strategy: 'end_sales_li' }
  });
  const [liTokenRunning, setLiTokenRunning] = useState(false);
  const [liTokenResult, setLiTokenResult] = useState(null);
  const [rePushModal, setRePushModal] = useState(null); // { destination: 'manyreach'|'crm', alreadyPushed: [...], freshUnpushed: [...] }
  const [editingStep, setEditingStep] = useState(null); // { contactId, channel, idx, body }
  const [savingStep, setSavingStep] = useState(false);
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [pendingGenerateCount, setPendingGenerateCount] = useState(0);
  const textareaRef = useRef(null);

  function startEditStep(contactId, channel, idx, currentBody) {
    setEditingStep({ contactId, channel, idx, body: currentBody || '' });
  }
  function cancelEditStep() {
    setEditingStep(null);
  }
  async function insertTokenAtCursor(token) {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    // Fetch a context-wrapped phrase from backend so the token reads naturally.
    // Backend resolves attachment_name from contact override → client default.
    let phrase = '{{' + token + '}}';
    try {
      const url = '/token-wrap?token=' + encodeURIComponent(token) + '&contact_id=' + encodeURIComponent(editingStep.contactId);
      const res = await fetch(API_BASE + url);
      if (res.ok) {
        const data = await res.json();
        if (data && data.wrapped) phrase = data.wrapped;
      }
    } catch (e) {
      // Fallback to bare token if endpoint fails
    }
    const before = editingStep.body.slice(0, start);
    const after = editingStep.body.slice(end);
    const newBody = before + phrase + after;
    setEditingStep(prev => ({ ...prev, body: newBody }));
    setTimeout(() => {
      ta.focus();
      const pos = start + phrase.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }
  async function saveEditStep() {
    if (!editingStep) return;
    setSavingStep(true);
    try {
      const seq = getSeq(editingStep.contactId);
      if (!seq) throw new Error('Sequence not found');
      const emailSeq = (typeof seq.email_sequence === 'string' ? JSON.parse(seq.email_sequence) : seq.email_sequence) || [];
      const linkedinSeq = (typeof seq.linkedin_sequence === 'string' ? JSON.parse(seq.linkedin_sequence) : seq.linkedin_sequence) || [];
      const targetSeq = editingStep.channel === 'email' ? emailSeq : linkedinSeq;
      const updated = [...targetSeq];
      const bodyKey = editingStep.channel === 'email' ? 'body' : 'message';
      updated[editingStep.idx] = { ...updated[editingStep.idx], [bodyKey]: editingStep.body };
      const token = await getToken();
      const res = await fetch(API_BASE + '/sequences/' + seq.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({
          email_sequence: editingStep.channel === 'email' ? updated : emailSeq,
          linkedin_sequence: editingStep.channel === 'linkedin' ? updated : linkedinSeq
        })
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Step saved');
      setEditingStep(null);
      await refreshSeqs();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setSavingStep(false);
  }

  const [creatingNewBatch, setCreatingNewBatch] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchDestination, setNewBatchDestination] = useState('pipedrive_csv');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  // Client Engine campaigns (separate from ManyReach campaigns above)
  const [ceCampaigns, setCeCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState({ has_sequence: null, email: null, linkedin: null, in_batch: null, push_state: null, score: null });
  // server-side rail filter (returns matching contact id set; intersected with client list)
  const [seqTree, setSeqTree] = useState(null);
  const [seqIdSet, setSeqIdSet] = useState(null); // null = inactive
  const [seqFilterLoading, setSeqFilterLoading] = useState(false);
  const [seqViews, setSeqViews] = useState([]);
  const [seqTargets, setSeqTargets] = useState({});
  const [batchMemberships, setBatchMemberships] = useState([]);
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openContactId, setOpenContactId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const stopRequestedRef = useRef(false);

  async function stopSequenceGeneration() {
    stopRequestedRef.current = true;
    try {
      await fetch(API_BASE + '/jobs/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'sequence', client_id: parseInt(id) })
      });
      toast('Stopping sequence generation...', { icon: '⏹️' });
    } catch (_) {}
  }
  const [genProgress, setGenProgress] = useState(null);
  const [approving, setApproving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState(null);
  const [campaign, setCampaign] = useState('');
  // Selected Client Engine campaign for Push to Campaign action
  const [ceCampaign, setCeCampaign] = useState('');
  const [pushingToCampaign, setPushingToCampaign] = useState(false);
  const [crmTarget, setCrmTarget] = useState('');
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => { fetchAll(); }, [id]);

  // Sequence rail: server filter -> matching contact id set (intersected with client list).
  const seqActive = seqTree && Array.isArray(seqTree.and) && seqTree.and.length > 0;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!seqTree || !Array.isArray(seqTree.and) || seqTree.and.length === 0) { setSeqIdSet(null); return; }
      setSeqFilterLoading(true);
      try {
        const token = await getToken();
        const res = await fetch(API_BASE + '/bulk/resolve-ids', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify({ entity: 'contacts', client_id: parseInt(id), filter: seqTree }),
        });
        const data = await res.json();
        if (!cancelled && res.ok) setSeqIdSet(new Set((data.ids || []).map(Number)));
        else if (!cancelled) setSeqIdSet(new Set());
      } catch (e) { if (!cancelled) setSeqIdSet(new Set()); }
      finally { if (!cancelled) setSeqFilterLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [seqTree, id, getToken]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(API_BASE + '/filter/contacts/targets/campaign?client_id=' + parseInt(id), { headers: { Authorization: 'Bearer ' + token } });
        if (res.ok) setSeqTargets({ campaign: await res.json() });
      } catch (_) {}
    })();
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(API_BASE + '/saved-filters/contacts?client_id=' + parseInt(id), { headers: { Authorization: 'Bearer ' + token } });
        if (res.ok) setSeqViews(await res.json());
      } catch (_) {}
    })();
  }, [id, getToken]);

  // Refresh when a sequence is generated from another screen (Prospects, Dashboard, ContactPanel)
  useEffect(() => {
    function onGenerated() { refreshSeqs(); }
    window.addEventListener('ce:sequenceGenerated', onGenerated);
    return () => window.removeEventListener('ce:sequenceGenerated', onGenerated);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const contactId = params.get('contactId');
    if (contactId) setOpenContactId(parseInt(contactId));
  }, [location.search]);

  async function fetchAll() {
    try {
      const [clientRes, contactsRes, seqsRes, campaignsRes, channelsRes, batchRes, ceCampsRes] = await Promise.all([
        fetch(API_BASE + '/clients/' + id),
        fetch(API_BASE + '/clients/' + id + '/contacts'),
        fetch(API_BASE + '/clients/' + id + '/sequences'),
        fetch(API_BASE + '/manyreach/campaigns'),
        fetch(API_BASE + '/clients/' + id + '/sequence-channels'),
        fetch(API_BASE + '/clients/' + id + '/batch-memberships'),
        fetch(API_BASE + '/clients/' + id + '/campaigns'),
      ]);
      setClient(await clientRes.json());
      const contacts = await contactsRes.json();
      setAllContacts(Array.isArray(contacts) ? contacts : []);
      const seqs = await seqsRes.json();
      setAllSequences(Array.isArray(seqs) ? seqs : []);
      const camps = await campaignsRes.json();
      setCampaigns(camps.items || camps.campaigns || camps || []);
      const chans = await channelsRes.json();
      setAllChannels(Array.isArray(chans) ? chans : []);
      const memberships = batchRes.ok ? await batchRes.json() : [];
      setBatchMemberships(Array.isArray(memberships) ? memberships : []);
      const ceCamps = ceCampsRes.ok ? await ceCampsRes.json() : [];
      setCeCampaigns(Array.isArray(ceCamps) ? ceCamps : (ceCamps.items || []));
    } catch (e) { setError('Failed to load.'); }
  }

  async function refreshSeqs() {
    const [seqs, chans] = await Promise.all([
      fetch(API_BASE + '/clients/' + id + '/sequences').then(r => r.json()),
      fetch(API_BASE + '/clients/' + id + '/sequence-channels').then(r => r.json()),
    ]);
    setAllSequences(Array.isArray(seqs) ? seqs : []);
    setAllChannels(Array.isArray(chans) ? chans : []);
  }

  function getSeq(contactId) { return allSequences.find(s => s.contact_id === contactId) || null; }
  function getChannel(contactId, channel) { return allChannels.find(c => c.contact_id === contactId && c.channel === channel) || null; }

  async function approveChannel(channelId) {
    try {
      const res = await fetch(API_BASE + '/sequence-channels/' + channelId + '/approve', { method: 'PUT' });
      if (!res.ok) throw new Error('Approve failed');
      await refreshSeqs();
      toast.success('Channel approved');
    } catch (e) {
      toast.error(e.message);
    }
  }

  const q = search.toLowerCase();
  const membershipMap = new Map(batchMemberships.map(m => [m.contact_id, m]));

  const matchesSearch = (c) => !q || c.full_name?.toLowerCase().includes(q) || c.job_title?.toLowerCase().includes(q) ||
    c.email?.toLowerCase().includes(q) || c.company_name?.toLowerCase().includes(q);

  const matchesChips = (c) => {
    const seq = getSeq(c.id);
    const linkedinCh = getChannel(c.id, 'linkedin');
    const emailCh = getChannel(c.id, 'email');
    const mb = membershipMap.get(c.id) || { in_linkedin_batch: false, in_email_batch: false };

    if (filterValues.has_sequence === 'has' && !seq) return false;
    if (filterValues.has_sequence === 'none' && seq) return false;

    if (filterValues.email === 'approved' && !(emailCh && emailCh.approval_status === 'approved')) return false;
    if (filterValues.email === 'pending'  && !(emailCh && emailCh.approval_status === 'pending')) return false;
    if (filterValues.email === 'none'     && emailCh) return false;

    if (filterValues.linkedin === 'approved' && !(linkedinCh && linkedinCh.approval_status === 'approved')) return false;
    if (filterValues.linkedin === 'pending'  && !(linkedinCh && linkedinCh.approval_status === 'pending')) return false;
    if (filterValues.linkedin === 'none'     && linkedinCh) return false;

    if (filterValues.in_batch === 'linkedin' && !mb.in_linkedin_batch) return false;
    if (filterValues.in_batch === 'email'    && !mb.in_email_batch) return false;
    if (filterValues.in_batch === 'none'     && (mb.in_linkedin_batch || mb.in_email_batch)) return false;

    if (filterValues.push_state === 'pushed' && !(seq && seq.pushed_at)) return false;
    if (filterValues.push_state === 'approved_unpushed' && !(seq && seq.approval_status === 'approved' && !seq.pushed_at)) return false;

    if (filterValues.score === 'high' && !(c.score >= 8)) return false;
    if (filterValues.score === 'mid'  && !(c.score >= 6 && c.score < 8)) return false;
    if (filterValues.score === 'low'  && !(c.score < 6 && c.score != null)) return false;
    if (filterValues.score === 'unscored' && c.score != null) return false;

    return true;
  };

  const filtered = allContacts.filter(c => matchesSearch(c) && matchesChips(c) && (!seqActive || (seqIdSet && seqIdSet.has(c.id))));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const searchOnly = allContacts.filter(matchesSearch);
  const sequencesFilters = [
    {
      key: 'has_sequence', label: 'Sequence',
      options: [
        { value: 'has',  label: 'Has sequence', count: searchOnly.filter(c => getSeq(c.id)).length },
        { value: 'none', label: 'No sequence',  count: searchOnly.filter(c => !getSeq(c.id)).length },
      ],
    },
    {
      key: 'email', label: 'Email',
      options: [
        { value: 'approved', label: 'Approved', count: searchOnly.filter(c => { const ch = getChannel(c.id, 'email'); return ch && ch.approval_status === 'approved'; }).length },
        { value: 'pending',  label: 'Pending',  count: searchOnly.filter(c => { const ch = getChannel(c.id, 'email'); return ch && ch.approval_status === 'pending'; }).length },
        { value: 'none',     label: 'None',     count: searchOnly.filter(c => !getChannel(c.id, 'email')).length },
      ],
    },
    {
      key: 'linkedin', label: 'LinkedIn',
      options: [
        { value: 'approved', label: 'Approved', count: searchOnly.filter(c => { const ch = getChannel(c.id, 'linkedin'); return ch && ch.approval_status === 'approved'; }).length },
        { value: 'pending',  label: 'Pending',  count: searchOnly.filter(c => { const ch = getChannel(c.id, 'linkedin'); return ch && ch.approval_status === 'pending'; }).length },
        { value: 'none',     label: 'None',     count: searchOnly.filter(c => !getChannel(c.id, 'linkedin')).length },
      ],
    },
    {
      key: 'in_batch', label: 'In batch',
      options: [
        { value: 'linkedin', label: 'In LinkedIn batch', count: searchOnly.filter(c => membershipMap.get(c.id)?.in_linkedin_batch).length },
        { value: 'email',    label: 'In Email batch',    count: searchOnly.filter(c => membershipMap.get(c.id)?.in_email_batch).length },
        { value: 'none',     label: 'Not in any batch',  count: searchOnly.filter(c => { const m = membershipMap.get(c.id); return !m?.in_linkedin_batch && !m?.in_email_batch; }).length },
      ],
    },
    {
      key: 'push_state', label: 'Push',
      options: [
        { value: 'approved_unpushed', label: 'Approved, not pushed', count: searchOnly.filter(c => { const s = getSeq(c.id); return s && s.approval_status === 'approved' && !s.pushed_at; }).length },
        { value: 'pushed',            label: 'Pushed to ManyReach',  count: searchOnly.filter(c => { const s = getSeq(c.id); return s && s.pushed_at; }).length },
      ],
    },
    {
      key: 'score', label: 'Score',
      options: [
        { value: 'high',     label: '8+',       count: searchOnly.filter(c => c.score >= 8).length },
        { value: 'mid',      label: '6–7',      count: searchOnly.filter(c => c.score >= 6 && c.score < 8).length },
        { value: 'low',      label: '<6',       count: searchOnly.filter(c => c.score < 6 && c.score != null).length },
        { value: 'unscored', label: 'Unscored', count: searchOnly.filter(c => c.score == null).length },
      ],
    },
  ];

  const selectedContacts = allContacts.filter(c => selectedIds.has(c.id));
  const needSequence = selectedContacts.filter(c => !getSeq(c.id));
  const pendingSelected = selectedContacts.filter(c => { const s = getSeq(c.id); return s && s.approval_status === 'pending'; });
  const approvedUnpushed = selectedContacts.filter(c => { const s = getSeq(c.id); return s && s.approval_status === 'approved' && !s.pushed_at; });
  const hasSelection = selectedIds.size > 0;

  function toggleSelect(e, contactId) {
    e.stopPropagation();
    const next = new Set(selectedIds);
    if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
    setSelectedIds(next);
  }
  function selectAll() { setSelectedIds(new Set(filtered.map(c => c.id))); }

  async function runBulkToken() {
    if (!selectedIds.size) return;
    const enabledTokens = Object.entries(tokenRows).filter(([_, v]) => v.enabled);
    if (enabledTokens.length === 0) { toast.error('Pick at least one token'); return; }
    setTokenRunning(true);
    setTokenResult(null);
    const perToken = [];
    try {
      const token = await getToken();
      for (const [tk, cfg] of enabledTokens) {
        const endpoint = tokenAction === 'insert' ? '/sequences/bulk-insert-token' : '/sequences/bulk-remove-token';
        const body = tokenAction === 'insert'
          ? { contact_ids: Array.from(selectedIds), token: tk, strategy: cfg.strategy }
          : { contact_ids: Array.from(selectedIds), token: tk };
        const res = await fetch(API_BASE + endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(tk + ' failed: ' + await res.text());
        const data = await res.json();
        perToken.push({ token: tk, ...data });
      }
      setTokenResult({ perToken, requested: selectedIds.size });
      const totalMods = perToken.reduce((a, r) => a + r.modified, 0);
      toast.success((tokenAction === 'insert' ? 'Inserted ' : 'Removed ') + enabledTokens.length + ' token type(s) · ' + totalMods + ' total updates');
      await refreshSeqs();
    } catch (e) {
      toast.error('Bulk token op failed: ' + e.message);
    }
    setTokenRunning(false);
  }

  async function runBulkLiToken() {
    if (!selectedIds.size) return;
    const enabledTokens = Object.entries(liTokenRows).filter(([_, v]) => v.enabled);
    if (enabledTokens.length === 0) { toast.error('Pick at least one token'); return; }
    setLiTokenRunning(true);
    setLiTokenResult(null);
    const perToken = [];
    try {
      const token = await getToken();
      for (const [tk, cfg] of enabledTokens) {
        const endpoint = liTokenAction === 'insert' ? '/sequences/bulk-insert-linkedin-token' : '/sequences/bulk-remove-linkedin-token';
        const body = liTokenAction === 'insert'
          ? { contact_ids: Array.from(selectedIds), token: tk, strategy: cfg.strategy }
          : { contact_ids: Array.from(selectedIds), token: tk };
        const res = await fetch(API_BASE + endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(tk + ' failed: ' + await res.text());
        const data = await res.json();
        perToken.push({ token: tk, ...data });
      }
      setLiTokenResult({ perToken, requested: selectedIds.size });
      const totalMods = perToken.reduce((a, r) => a + r.modified, 0);
      toast.success((liTokenAction === 'insert' ? 'Inserted ' : 'Removed ') + enabledTokens.length + ' LinkedIn token type(s) · ' + totalMods + ' total updates');
      await refreshSeqs();
    } catch (e) {
      toast.error('Bulk LinkedIn token op failed: ' + e.message);
    }
    setLiTokenRunning(false);
  }

  function selectPage() { setSelectedIds(new Set(paged.map(c => c.id))); }
  function clearAll() { setSelectedIds(new Set()); }

  function handleGenerate() {
    const toGenerate = needSequence.length > 0 ? needSequence : selectedContacts;
    if (!toGenerate.length) return;
    setPendingGenerateCount(toGenerate.length);
    setConfirmGenerateOpen(true);
  }

  async function doGenerate() {
    setConfirmGenerateOpen(false);
    const toGenerate = needSequence.length > 0 ? needSequence : selectedContacts;
    if (!toGenerate.length) return;
    stopRequestedRef.current = false;
    setGenerating(true); setGenProgress({ current: 0, total: toGenerate.length, status: 'Preparing...' });
    const genToastId = toast.loading('Generating ' + toGenerate.length + ' sequence' + (toGenerate.length === 1 ? '' : 's') + ' with AI...');
    let done = 0;
    for (let i = 0; i < toGenerate.length; i += 3) {
      if (stopRequestedRef.current) break;
      const batch = toGenerate.slice(i, i + 3);
      await Promise.all(batch.map(async contact => {
        if (stopRequestedRef.current) return;
        try {
          const res = await creditAwareFetch(API_BASE + '/contacts/' + contact.id + '/generate-sequence', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ client_id: id, prompt })
          });
         if (!res) return;
          if (!res) return; // 402 — modal handled by creditAwareFetch
          if (res.status === 499) return; // stopped by user
          if (!res.ok) throw new Error();
          const data = await res.json();
          await fetch(API_BASE + '/contacts/' + contact.id + '/sequences', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ client_id: id, email_sequence: data.email_sequence, linkedin_sequence: data.linkedin_sequence, call_sequence: data.call_sequence, prompt_used: prompt })
          });
          done++;
          setGenProgress({ current: done, total: toGenerate.length, status: 'Generated ' + done + ' of ' + toGenerate.length });
        } catch (e) {}
      }));
    }
    const wasStopped = stopRequestedRef.current;
    stopRequestedRef.current = false;
    setGenProgress({ current: done, total: toGenerate.length, status: wasStopped ? 'Stopped.' : 'Complete.' });
    if (wasStopped) {
      toast('Stopped after ' + done + ' of ' + toGenerate.length, { id: genToastId, icon: '⏹️' });
    } else if (done === toGenerate.length) {
      toast.success('Generated ' + done + ' sequence' + (done === 1 ? '' : 's'), { id: genToastId });
    } else if (done === 0) {
      toast.error('Could not generate any sequences', { id: genToastId });
    } else {
      toast.success('Generated ' + done + ' of ' + toGenerate.length + ' (some failed)', { id: genToastId });
    }
    await refreshSeqs(); setGenerating(false);
  }

  // Per-channel counters for action bar
  const linkedinApproved = selectedContacts.filter(c => {
    const ch = getChannel(c.id, 'linkedin');
    return ch && ch.approval_status === 'approved';
  });
  const emailApproved = selectedContacts.filter(c => {
    const ch = getChannel(c.id, 'email');
    return ch && ch.approval_status === 'approved';
  });

  async function loadAvailableBatches(channel) {
    try {
      const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
      const res = await fetch(`${API_BASE}/clients/${id}/${ep}`);
      if (!res.ok) { setAvailableBatches([]); return; }
      const data = await res.json();
      setAvailableBatches(Array.isArray(data) ? data.filter(b => b.status !== 'exported') : []);
    } catch (e) {
      setAvailableBatches([]);
    }
  }

  function openAddToBatch() {
    if (!selectedIds.size) return;
    // Default to whichever channel has more approved contacts in current selection
    const defaultChannel = linkedinApproved.length >= emailApproved.length ? 'linkedin' : 'email';
    setAddToBatchChannel(defaultChannel);
    setAddToBatchOpen(true);
    setCreatingNewBatch(false);
    setSelectedBatchId('');
    setNewBatchName('');
    loadAvailableBatches(defaultChannel);
  }

  function closeAddToBatch() {
    setAddToBatchOpen(false);
    setBatchSubmitting(false);
  }

  function changeBatchChannel(channel) {
    setAddToBatchChannel(channel);
    setSelectedBatchId('');
    loadAvailableBatches(channel);
  }

  async function submitAddToBatch() {
    if (!selectedIds.size) return;
    setBatchSubmitting(true);
    try {
      const channel = addToBatchChannel;
      const eligibleContacts = channel === 'linkedin' ? linkedinApproved : emailApproved;
      const skipped = selectedContacts.length - eligibleContacts.length;

      if (eligibleContacts.length === 0) {
        toast.error(`No eligible contacts. ${skipped} skipped — need approved ${channel} sequences.`);
        setBatchSubmitting(false);
        return;
      }

      let batchId = selectedBatchId;
      if (creatingNewBatch) {
        if (!newBatchName.trim()) { toast.error('Batch name required'); setBatchSubmitting(false); return; }
        const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
        const body = channel === 'email'
          ? { name: newBatchName.trim(), destination_type: newBatchDestination }
          : { name: newBatchName.trim() };
        const createRes = await fetch(`${API_BASE}/clients/${id}/${ep}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!createRes.ok) { toast.error('Failed to create batch'); setBatchSubmitting(false); return; }
        const created = await createRes.json();
        batchId = created.id;
      }

      if (!batchId) { toast.error('Pick a batch or create a new one'); setBatchSubmitting(false); return; }

      const ep = channel === 'email' ? 'email-batches' : 'linkedin-batches';
      let added = 0, failed = 0;
      for (const contact of eligibleContacts) {
        const ch = getChannel(contact.id, channel);
        if (!ch) { failed++; continue; }
        try {
          const res = await fetch(`${API_BASE}/${ep}/${batchId}/contacts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contact_id: contact.id, sequence_id: ch.sequence_id }),
          });
          if (res.ok) added++; else failed++;
        } catch (e) { failed++; }
      }

      const parts = [`Added ${added}`];
      if (failed > 0) parts.push(`${failed} failed`);
      if (skipped > 0) parts.push(`${skipped} skipped (no approved ${channel})`);
      toast.success(parts.join(' · '));

      closeAddToBatch();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setBatchSubmitting(false);
    }
  }

  async function handleApprove() {
    setApproving(true); const apprCount = pendingSelected.length;
    const apprToastId = toast.loading('Approving ' + apprCount + ' sequence' + (apprCount === 1 ? '' : 's') + '...');
    const token = await getToken();
    await Promise.all(pendingSelected.map(c => { const s = getSeq(c.id); return s ? fetch(API_BASE + '/sequences/' + s.id + '/approve', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token } }) : Promise.resolve(); }));
    await refreshSeqs();
    toast.success('Approved ' + apprCount + ' sequence' + (apprCount === 1 ? '' : 's'), { id: apprToastId });
    setApproving(false);
  }

  async function handlePushToCampaign() {
    // Push selected sequences into a Client Engine campaign.
    // Uses POST /sequences/:id/approve with { campaign_id } which approves AND enrols
    // in one call. Pending sequences are auto-approved; already-approved sequences
    // are reassigned to the chosen campaign.
    if (!ceCampaign) return;
    const toPush = selectedContacts.filter(c => !!getSeq(c.id));
    if (!toPush.length) { toast.error('No sequences in selection'); return; }
    setPushingToCampaign(true);
    const toastId = toast.loading('Enrolling ' + toPush.length + ' into campaign...');
    let ok = 0, failed = 0;
    const token = await getToken();
    for (const contact of toPush) {
      const s = getSeq(contact.id);
      if (!s) { failed++; continue; }
      try {
        const res = await fetch(API_BASE + '/sequences/' + s.id + '/approve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
          body: JSON.stringify({ campaign_id: parseInt(ceCampaign) }),
        });
        if (res.ok) ok++; else failed++;
      } catch (e) { failed++; }
    }
    await refreshSeqs();
    setPushingToCampaign(false);
    if (failed === 0) toast.success('Enrolled ' + ok + ' into campaign', { id: toastId });
    else toast.error('Enrolled ' + ok + ', ' + failed + ' failed', { id: toastId });
  }

  async function handlePushManyReach(opts = {}) {
    const forceRePush = opts.forceRePush === true;
    // Detect already-pushed sequences in the selection
    let toPush = approvedUnpushed;
    if (!forceRePush) {
      const alreadyPushed = selectedContacts.filter(c => { const s = getSeq(c.id); return s && s.approval_status === 'approved' && s.pushed_at; });
      const freshUnpushed = approvedUnpushed;
      if (alreadyPushed.length > 0) {
        setRePushModal({ destination: 'manyreach', alreadyPushed, freshUnpushed });
        return;
      }
      if (freshUnpushed.length === 0) {
        toast.error('No approved sequences selected to push. Approve sequences first.');
        return;
      }
    } else {
      // Re-push confirmed: include both alreadyPushed and freshUnpushed.
      const fresh = Array.isArray(opts.freshUnpushed) ? opts.freshUnpushed : [];
      const already = Array.isArray(opts.alreadyPushed) ? opts.alreadyPushed : [];
      toPush = [...fresh, ...already];
    }
    if (!campaign || !toPush.length) return;
    setPushing(true); setPushProgress({ current: 0, total: toPush.length, status: 'Starting...' });
    const pushToastId = toast.loading('Pushing ' + toPush.length + ' to ManyReach...');
    let pushed = 0, failed = 0;
    const failureLog = [];
    for (let i = 0; i < toPush.length; i++) {
      const contact = toPush[i];
      const s = getSeq(contact.id);
      if (!s) {
        setPushProgress(prev => ({ current: prev.current + 1, total: toPush.length, status: 'Pushing ' + (prev.current + 1) + ' of ' + toPush.length + '...' }));
        continue;
      }
      try {
        const token = await getToken();
        const res = await fetch(API_BASE + '/sequences/' + s.id + '/push-to-manyreach', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ campaign_id: campaign })
        });
        if (res.ok) {
          pushed++;
        } else {
          failed++;
          let body = null;
          try { body = await res.json(); } catch (_) {}
          failureLog.push({ seq_id: s.id, email: contact.email, http: res.status, mr_status: body && body.manyreach_status, mr_body: body && body.manyreach_body });
        }
      } catch (e) {
        failed++;
        failureLog.push({ seq_id: s.id, email: contact.email, http: 'network', error: e && e.message });
      }
      setPushProgress(prev => ({ current: prev.current + 1, total: toPush.length, status: 'Pushing ' + (prev.current + 1) + ' of ' + toPush.length + '...' }));
      // ManyReach rate limit: 60 req/min. Pace at ~1.1s per request to stay safely under.
      if (i + 1 < toPush.length) await new Promise(r => setTimeout(r, 1100));
    }
    if (failureLog.length > 0) {
      const breakdown = failureLog.reduce((acc, f) => { const k = f.mr_status || f.http; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
      console.log('[manyreach-push] FAILURE BREAKDOWN:', breakdown);
      console.log('[manyreach-push] FAILURES (full):', failureLog);
    }
    await refreshSeqs();
    if (failed === 0) {
      toast.success('Pushed ' + pushed + ' to ManyReach', { id: pushToastId });
    } else if (pushed === 0) {
      toast.error('Push failed for all ' + failed + ' contacts', { id: pushToastId });
    } else {
      toast.success('Pushed ' + pushed + ', ' + failed + ' failed', { id: pushToastId });
    }
    setPushProgress(null); setPushing(false);
  }

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Page wrapper */}
      <div style={{ padding: '1.5rem 2rem', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem', display: 'block' }}>
          {client ? '← ' + client.name : '← Back'}
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Sequences</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{client ? client.name : ''}</p>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#888', textAlign: 'right' }}>
            <p style={{ margin: 0 }}>{allContacts.length} contacts</p>
            <p style={{ margin: 0 }}>{allSequences.length} sequences</p>
          </div>
        </div>

        {error && <p style={{ color: '#C53030', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</p>}

        {/* Smart action bar */}
        {hasSelection && (
          <div style={{ backgroundColor: '#1A1A1A', borderRadius: '10px', padding: '0.85rem 1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.82rem', color: '#fff', fontWeight: '500', flexShrink: 0 }}>
              {selectedIds.size} selected
              {needSequence.length > 0 && <span style={{ color: '#FBD38D', marginLeft: '0.5rem' }}>· {needSequence.length} need sequences</span>}
              {pendingSelected.length > 0 && <span style={{ color: '#9AE6B4', marginLeft: '0.5rem' }}>· {pendingSelected.length} pending approval</span>}
              {approvedUnpushed.length > 0 && <span style={{ color: '#90CDF4', marginLeft: '0.5rem' }}>· {approvedUnpushed.length} ready to push</span>}
              {linkedinApproved.length > 0 && <span style={{ color: '#BFDBFE', marginLeft: '0.5rem' }}>· {linkedinApproved.length} approved LinkedIn</span>}
              {emailApproved.length > 0 && <span style={{ color: '#BFDBFE', marginLeft: '0.5rem' }}>· {emailApproved.length} approved email</span>}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginLeft: 'auto', alignItems: 'center' }}>
              <input value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Optional instructions..."
                style={{ fontSize: '0.75rem', border: '1px solid #555', borderRadius: '6px', padding: '0.35rem 0.6rem', backgroundColor: '#333', color: '#fff', outline: 'none', width: '160px' }} />
              <button onClick={handleGenerate} disabled={generating}
                style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', opacity: generating ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                {generating ? 'Generating...' : needSequence.length > 0 ? 'Generate ' + needSequence.length : 'Regenerate ' + selectedIds.size}
              </button>
              {generating && (
                <button onClick={stopSequenceGeneration}
                  style={{ fontSize: '0.78rem', color: '#fff', background: '#B7791F', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Stop
                </button>
              )}
              {pendingSelected.length > 0 && (
                <button onClick={handleApprove} disabled={approving}
                  style={{ fontSize: '0.78rem', color: '#1A1A1A', background: '#9AE6B4', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', opacity: approving ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                  {approving ? 'Approving...' : 'Approve ' + pendingSelected.length}
                </button>
              )}
              <button onClick={openAddToBatch}
                disabled={linkedinApproved.length + emailApproved.length === 0}
                title={linkedinApproved.length + emailApproved.length === 0 ? 'Approve sequences first to add them to a batch' : ''}
                style={{ fontSize: '0.78rem', color: '#fff', background: linkedinApproved.length + emailApproved.length === 0 ? '#3A3A3A' : '#1A56A0', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: linkedinApproved.length + emailApproved.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap', opacity: linkedinApproved.length + emailApproved.length === 0 ? 0.55 : 1 }}>
                Add to batch ({linkedinApproved.length + emailApproved.length} ready)
              </button>
              <button onClick={() => { setTokenAction('insert'); setTokenRows({ cta_url: { enabled: false, strategy: 'end_sales' }, landing_page: { enabled: false, strategy: 'end_sales' }, attachment: { enabled: false, strategy: 'end_sales' } }); setTokenResult(null); setTokenModalOpen(true); }}
                style={{ fontSize: '0.78rem', color: '#fff', background: '#553C9A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
                Link tokens
              </button>
              <button onClick={() => { setLiTokenAction('insert'); setLiTokenRows({ cta_url: { enabled: false, strategy: 'end_sales_li' }, landing_page: { enabled: false, strategy: 'end_sales_li' }, attachment: { enabled: false, strategy: 'end_sales_li' } }); setLiTokenResult(null); setLiTokenModalOpen(true); }}
                style={{ fontSize: '0.78rem', color: '#fff', background: '#0A66C2', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
                LinkedIn token
              </button>
              <select value={ceCampaign} onChange={e => setCeCampaign(e.target.value)}
                style={{ fontSize: '0.75rem', border: '1px solid #555', borderRadius: '6px', padding: '0.35rem 0.5rem', backgroundColor: '#333', color: '#fff', outline: 'none' }}>
                <option value="">CE Campaign...</option>
                {ceCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button onClick={() => handlePushToCampaign()} disabled={pushingToCampaign || !ceCampaign}
                style={{ fontSize: '0.78rem', color: '#fff', background: '#2F855A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', opacity: pushingToCampaign || !ceCampaign ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                {pushingToCampaign ? 'Pushing...' : 'Push to Campaign'}
              </button>
              <select value={campaign} onChange={e => setCampaign(e.target.value)}
                style={{ fontSize: '0.75rem', border: '1px solid #555', borderRadius: '6px', padding: '0.35rem 0.5rem', backgroundColor: '#333', color: '#fff', outline: 'none' }}>
                <option value="">Campaign...</option>
                {campaigns.map(c => <option key={c.campaignId} value={c.campaignId}>{c.name || c.title}</option>)}
              </select>
                  <button onClick={() => handlePushManyReach()} disabled={pushing || !campaign}
                    style={{ fontSize: '0.78rem', color: '#1A1A1A', background: '#90CDF4', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '500', opacity: pushing || !campaign ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                    {pushing ? 'Pushing...' : 'Push to ManyReach'}
                  </button>
                  <select value={crmTarget} onChange={e => setCrmTarget(e.target.value)}
                    style={{ fontSize: '0.75rem', border: '1px solid #555', borderRadius: '6px', padding: '0.35rem 0.5rem', backgroundColor: '#333', color: '#fff', outline: 'none' }}>
                    <option value="">CRM...</option>
                    <option value="pipedrive">Pipedrive</option>
                    <option value="bigin">Zoho Bigin</option>
                    <option value="hubspot">HubSpot</option>
                    <option value="salesforce">Salesforce</option>
                    <option value="zohocrm">Zoho CRM</option>
                    <option value="monday">Monday CRM</option>
                    <option value="copper">Copper</option>
                    <option value="close">Close</option>
                  </select>
                  <button onClick={() => alert('CRM push coming in v2')} disabled={!crmTarget}
                    style={{ fontSize: '0.78rem', color: '#1A1A1A', background: '#BEE3F8', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: !crmTarget ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: !crmTarget ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                    Push to CRM
                  </button>

              <button onClick={clearAll} style={{ fontSize: '0.75rem', color: '#AAA', background: 'none', border: '1px solid #555', borderRadius: '6px', padding: '0.35rem 0.6rem', cursor: 'pointer' }}>Clear</button>
            </div>
          </div>
        )}

        {/* Progress bars */}
        {genProgress && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ backgroundColor: '#E8E4E0', borderRadius: '4px', height: '5px', overflow: 'hidden', marginBottom: '0.2rem' }}>
              <div style={{ backgroundColor: '#8B1A1A', height: '100%', width: (genProgress.current / genProgress.total * 100) + '%', transition: 'width 0.3s' }} />
            </div>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#888' }}>{genProgress.status}</p>
          </div>
        )}
        {pushProgress && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ backgroundColor: '#E8E4E0', borderRadius: '4px', height: '5px', overflow: 'hidden', marginBottom: '0.2rem' }}>
              <div style={{ backgroundColor: '#1A56A0', height: '100%', width: (pushProgress.current / pushProgress.total * 100) + '%', transition: 'width 0.3s' }} />
            </div>
            <p style={{ margin: 0, fontSize: '0.72rem', color: '#888' }}>{pushProgress.status}</p>
          </div>
        )}
        {/* Filter rail + content */}
        <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
        <FilterRail
          schema={SEQUENCE_SCHEMA}
          value={seqTree}
          onChange={(t) => { setSeqTree(t); setPage(0); }}
          matchCount={seqActive ? filtered.length : undefined}
          loading={seqFilterLoading}
          savedViews={seqViews}
          targets={seqTargets}
          onApplyView={(v) => { setSeqTree(v.tree); setPage(0); }}
        />
        {/* Two column layout: contact list + accordion */}
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: selectedIds.size > 1 ? '1fr 600px' : '1fr', gap: '1.5rem', alignItems: 'flex-start' }}>

          {/* Left: contact list */}
          <div>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search contacts by name, title, email, or company..."
                style={{ flex: 1, minWidth: '220px', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', outline: 'none', color: '#1A1A1A', backgroundColor: '#fff' }} />
              <button onClick={selectPage} style={{ fontSize: '0.72rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>Select page</button>
              <button onClick={selectAll} style={{ fontSize: '0.72rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>Select all {filtered.length}</button>
              {hasSelection && <button onClick={clearAll} style={{ fontSize: '0.72rem', color: '#C53030', background: 'none', border: '1px solid #FEB2B2', borderRadius: '20px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Clear</button>}
            </div>

            <p style={{ margin: '0 0 0.75rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>{filtered.length} contacts</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {paged.map(contact => {
                const seq = getSeq(contact.id);
                const isSelected = selectedIds.has(contact.id);
                return (
                  <div key={contact.id}
                    style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid ' + (isSelected ? '#8B1A1A' : '#E8E4E0'), borderRadius: '8px', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer' }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                    <input type="checkbox" checked={isSelected} onChange={e => toggleSelect(e, contact.id)}
                      style={{ flexShrink: 0, accentColor: '#8B1A1A', width: '15px', height: '15px', cursor: 'pointer' }} />
                    <div style={{ flex: 1, minWidth: 0 }} onClick={() => setOpenContactId(contact.id)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.15rem', flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{contact.full_name}</p>
                        <ChannelBadges emailCh={getChannel(contact.id, 'email')} linkedinCh={getChannel(contact.id, 'linkedin')} />
                      </div>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>
                        {contact.job_title}{contact.company_name && <span style={{ color: '#8B1A1A' }}> · {contact.company_name}</span>}
                      </p>
                      {contact.email && <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: '#AAA' }}>{contact.email}</p>}
                    </div>
                    <span onClick={() => setOpenContactId(contact.id)} style={{ fontSize: '0.75rem', color: '#8B1A1A', flexShrink: 0, cursor: 'pointer' }}>View →</span>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '3rem', textAlign: 'center' }}>
                  <p style={{ margin: 0, color: '#AAA', fontSize: '0.85rem' }}>No contacts found.</p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', marginTop: '0.5rem' }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                style={{ background: 'none', border: 'none', color: page === 0 ? '#CCC' : '#888', fontSize: '0.78rem', cursor: page === 0 ? 'default' : 'pointer' }}>← Prev</button>
              <span style={{ fontSize: '0.75rem', color: '#888' }}>{Math.min(page * PAGE_SIZE + 1, filtered.length)}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= filtered.length}
                style={{ background: 'none', border: 'none', color: (page + 1) * PAGE_SIZE >= filtered.length ? '#CCC' : '#888', fontSize: '0.78rem', cursor: (page + 1) * PAGE_SIZE >= filtered.length ? 'default' : 'pointer' }}>Next →</button>
            </div>
          </div>

          {/* Right: accordion for multiple selected */}
          {selectedIds.size > 1 && (
            <div style={{ position: 'sticky', top: '1.5rem' }}>
              <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ padding: '0.75rem 1.25rem', borderBottom: '0.5px solid #E8E4E0', backgroundColor: '#1A1A1A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '600' }}>{selectedIds.size} contacts — sequences</p>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '0.5rem' }}>
                  {selectedContacts.map(contact => {
                    const seq = getSeq(contact.id);
                    const emailSteps = seq ? (typeof seq.email_sequence === 'string' ? JSON.parse(seq.email_sequence) : seq.email_sequence) || [] : [];
                    const linkedinSteps = seq ? (typeof seq.linkedin_sequence === 'string' ? JSON.parse(seq.linkedin_sequence) : seq.linkedin_sequence) || [] : [];
                    return (
                      <details key={contact.id} style={{ border: '0.5px solid #E8E4E0', borderLeft: '3px solid ' + (seq ? (seq.approval_status === 'approved' ? '#276749' : '#B7791F') : '#D4CFC9'), borderRadius: '6px', marginBottom: '0.4rem', overflow: 'hidden', backgroundColor: '#fff' }}>
                        <summary style={{ padding: '0.65rem 0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', userSelect: 'none', listStyle: 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                              <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '500', color: '#1A1A1A' }}>{contact.full_name}</p>
                              <ChannelBadges emailCh={getChannel(contact.id, 'email')} linkedinCh={getChannel(contact.id, 'linkedin')} />
                            </div>
                            <p style={{ margin: '0.1rem 0 0', fontSize: '0.7rem', color: '#888' }}>{contact.job_title}{contact.company_name && ' · ' + contact.company_name}</p>
                          </div>
                          <span style={{ fontSize: '0.68rem', color: '#888', flexShrink: 0 }}>{emailSteps.length > 0 ? emailSteps.length + ' email + ' + linkedinSteps.length + ' LinkedIn' : 'No sequence'}</span>
                        </summary>
                        <div style={{ borderTop: '0.5px solid #E8E4E0', padding: '0.65rem 0.85rem' }}>
                          {emailSteps.length === 0 ? (
                            <p style={{ margin: 0, fontSize: '0.78rem', color: '#AAA' }}>No sequence. Select and click Generate.</p>
                          ) : (
                            <>
                              {/* Email steps */}
                              {(() => {
                                const ch = getChannel(contact.id, 'email');
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 0.5rem' }}>
                                    <p style={{ margin: 0, fontSize: '0.65rem', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                      Email {ch && <span style={{ marginLeft: '0.4rem', color: ch.approval_status === 'approved' ? '#276749' : '#B7791F', fontSize: '0.6rem' }}>· {ch.approval_status}</span>}
                                    </p>
                                    {ch && ch.approval_status !== 'approved' && (
                                      <button onClick={() => approveChannel(ch.id)}
                                        style={{ fontSize: '0.65rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '4px', padding: '0.15rem 0.5rem', cursor: 'pointer', fontWeight: '500' }}>
                                        Approve email
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                              {emailSteps.map((step, i) => {
                                const isEditing = editingStep && editingStep.contactId === contact.id && editingStep.channel === 'email' && editingStep.idx === i;
                                return (
                                <div key={i} style={{ marginBottom: '0.65rem', paddingBottom: '0.65rem', borderBottom: i < emailSteps.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.1rem' }}>
                                    <p style={{ margin: 0, fontSize: '0.65rem', color: '#AAA', textTransform: 'uppercase' }}>Step {step.step}</p>
                                    {!isEditing && (
                                      <button onClick={() => startEditStep(contact.id, 'email', i, step.body)}
                                        style={{ fontSize: '0.62rem', color: '#553C9A', background: 'none', border: '1px solid #D6BCFA', borderRadius: '4px', padding: '0.1rem 0.4rem', cursor: 'pointer' }}>
                                        Edit
                                      </button>
                                    )}
                                  </div>
                                  {step.subject && <p style={{ margin: '0 0 0.1rem', fontSize: '0.78rem', fontWeight: '600', color: '#1A1A1A' }}>{step.subject}</p>}
                                  {isEditing ? (
                                    <div>
                                      <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                                        <button onClick={() => insertTokenAtCursor('cta_url')} type="button"
                                          style={{ fontSize: '0.65rem', color: '#553C9A', background: '#FAF8FF', border: '1px solid #D6BCFA', borderRadius: '4px', padding: '0.15rem 0.4rem', cursor: 'pointer', fontFamily: 'monospace' }}>+ {'{{cta_url}}'}</button>
                                        <button onClick={() => insertTokenAtCursor('landing_page')} type="button"
                                          style={{ fontSize: '0.65rem', color: '#553C9A', background: '#FAF8FF', border: '1px solid #D6BCFA', borderRadius: '4px', padding: '0.15rem 0.4rem', cursor: 'pointer', fontFamily: 'monospace' }}>+ {'{{landing_page}}'}</button>
                                        <button onClick={() => insertTokenAtCursor('attachment')} type="button"
                                          style={{ fontSize: '0.65rem', color: '#553C9A', background: '#FAF8FF', border: '1px solid #D6BCFA', borderRadius: '4px', padding: '0.15rem 0.4rem', cursor: 'pointer', fontFamily: 'monospace' }}>+ {'{{attachment}}'}</button>
                                      </div>
                                      <textarea ref={textareaRef} value={editingStep.body}
                                        onChange={e => setEditingStep(prev => ({ ...prev, body: e.target.value }))}
                                        style={{ width: '100%', minHeight: '160px', fontSize: '0.75rem', color: '#4A5568', lineHeight: '1.45', padding: '0.5rem', border: '1px solid #D6BCFA', borderRadius: '6px', outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                                      <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.35rem' }}>
                                        <button onClick={saveEditStep} disabled={savingStep}
                                          style={{ fontSize: '0.7rem', color: '#fff', background: '#553C9A', border: 'none', borderRadius: '4px', padding: '0.25rem 0.7rem', cursor: 'pointer', fontWeight: '500', opacity: savingStep ? 0.6 : 1 }}>
                                          {savingStep ? 'Saving...' : 'Save'}
                                        </button>
                                        <button onClick={cancelEditStep} disabled={savingStep}
                                          style={{ fontSize: '0.7rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', padding: '0.25rem 0.7rem', cursor: 'pointer' }}>
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#4A5568', lineHeight: '1.45', whiteSpace: 'pre-wrap' }}>{step.body}</p>
                                  )}
                                </div>
                                );
                              })}
                              {/* LinkedIn steps */}
                              {linkedinSteps.length > 0 && (
                                <>
                                  {(() => {
                                    const ch = getChannel(contact.id, 'linkedin');
                                    return (
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0.75rem 0 0.5rem' }}>
                                        <p style={{ margin: 0, fontSize: '0.65rem', fontWeight: '700', color: '#1A56A0', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                          LinkedIn {ch && <span style={{ marginLeft: '0.4rem', color: ch.approval_status === 'approved' ? '#276749' : '#B7791F', fontSize: '0.6rem' }}>· {ch.approval_status}</span>}
                                        </p>
                                        {ch && ch.approval_status !== 'approved' && (
                                          <button onClick={() => approveChannel(ch.id)}
                                            style={{ fontSize: '0.65rem', color: '#fff', background: '#1A56A0', border: 'none', borderRadius: '4px', padding: '0.15rem 0.5rem', cursor: 'pointer', fontWeight: '500' }}>
                                            Approve LinkedIn
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                  {linkedinSteps.map((step, i) => {
                                    const isEditingLi = editingStep && editingStep.contactId === contact.id && editingStep.channel === 'linkedin' && editingStep.idx === i;
                                    return (
                                    <div key={i} style={{ marginBottom: '0.65rem', paddingBottom: '0.65rem', borderBottom: i < linkedinSteps.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.1rem' }}>
                                        <p style={{ margin: 0, fontSize: '0.65rem', color: '#AAA', textTransform: 'uppercase' }}>Step {step.step}{step.type ? ' — ' + step.type.replace(/_/g, ' ') : ''}</p>
                                        {!isEditingLi && (
                                          <button onClick={() => startEditStep(contact.id, 'linkedin', i, step.message)}
                                            style={{ fontSize: '0.62rem', color: '#1A56A0', background: 'none', border: '1px solid #BFDBFE', borderRadius: '4px', padding: '0.1rem 0.4rem', cursor: 'pointer' }}>
                                            Edit
                                          </button>
                                        )}
                                      </div>
                                      {isEditingLi ? (
                                        <div>
                                          <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '0.35rem' }}>Tokens are inserted on emails only — LinkedIn DMs stay plain text per platform.</div>
                                          <textarea ref={textareaRef} value={editingStep.body}
                                            onChange={e => setEditingStep(prev => ({ ...prev, body: e.target.value }))}
                                            style={{ width: '100%', minHeight: '120px', fontSize: '0.75rem', color: '#4A5568', lineHeight: '1.45', padding: '0.5rem', border: '1px solid #BFDBFE', borderRadius: '6px', outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                                          <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.35rem' }}>
                                            <button onClick={saveEditStep} disabled={savingStep}
                                              style={{ fontSize: '0.7rem', color: '#fff', background: '#1A56A0', border: 'none', borderRadius: '4px', padding: '0.25rem 0.7rem', cursor: 'pointer', fontWeight: '500', opacity: savingStep ? 0.6 : 1 }}>
                                              {savingStep ? 'Saving...' : 'Save'}
                                            </button>
                                            <button onClick={cancelEditStep} disabled={savingStep}
                                              style={{ fontSize: '0.7rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', padding: '0.25rem 0.7rem', cursor: 'pointer' }}>
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#4A5568', lineHeight: '1.45', whiteSpace: 'pre-wrap' }}>{step.message}</p>
                                      )}
                                    </div>
                                    );
                                  })}
                                </>
                              )}
                              {seq && seq.approval_status !== 'approved' && (
                                <button onClick={async () => { const token = await getToken(); await fetch(API_BASE + '/sequences/' + seq.id + '/approve', { method: 'POST', headers: { 'Authorization': 'Bearer ' + token } }); await refreshSeqs(); }}
                                  style={{ fontSize: '0.72rem', color: '#fff', background: '#276749', border: 'none', borderRadius: '6px', padding: '0.25rem 0.6rem', cursor: 'pointer', fontWeight: '500', marginTop: '0.25rem' }}>
                                  Approve
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {rePushModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setRePushModal(null)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '520px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#1A1A1A' }}>Confirm re-push</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#4A5568' }}>
              {rePushModal.alreadyPushed.length} of your {rePushModal.alreadyPushed.length + rePushModal.freshUnpushed.length} selected sequence(s) were already pushed to ManyReach. Re-pushing creates duplicate prospect records.
            </p>
            <div style={{ background: '#FFF5F5', border: '1px solid #FEB2B2', borderRadius: '6px', padding: '0.7rem 0.85rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#742A2A' }}>
              <div style={{ fontWeight: '600', marginBottom: '0.35rem' }}>Already pushed ({rePushModal.alreadyPushed.length}):</div>
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {rePushModal.alreadyPushed.map(c => {
                  const s = getSeq(c.id);
                  const pushedDate = s && s.pushed_at ? new Date(s.pushed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'unknown';
                  return (<div key={c.id} style={{ fontSize: '0.75rem', marginBottom: '0.2rem' }}>{c.full_name} — pushed {pushedDate}</div>);
                })}
              </div>
            </div>
            {rePushModal.freshUnpushed.length > 0 && (
              <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#4A5568' }}>
                {rePushModal.freshUnpushed.length} other selected sequence(s) have not been pushed yet — those will push normally.
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setRePushModal(null)}
                style={{ fontSize: '0.82rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => { const m = rePushModal; setRePushModal(null); handlePushManyReach({ forceRePush: true, alreadyPushed: m.alreadyPushed, freshUnpushed: m.freshUnpushed }); }}
                style={{ fontSize: '0.82rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: 'pointer', fontWeight: '500' }}>
                Re-push anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {tokenModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !tokenRunning && setTokenModalOpen(false)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '520px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem', color: '#1A1A1A' }}>Link tokens — bulk action</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#888' }}>{selectedIds.size} sequences selected. The sending tool replaces tokens with the URLs you set on the client (or per-contact override) at send time.</p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Action</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setTokenAction('insert')} style={{ flex: 1, fontSize: '0.8rem', color: tokenAction === 'insert' ? '#fff' : '#1A1A1A', background: tokenAction === 'insert' ? '#553C9A' : '#F5F5F5', border: '1px solid ' + (tokenAction === 'insert' ? '#553C9A' : '#D4CFC9'), borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontWeight: '500' }}>Insert</button>
                <button onClick={() => setTokenAction('remove')} style={{ flex: 1, fontSize: '0.8rem', color: tokenAction === 'remove' ? '#fff' : '#1A1A1A', background: tokenAction === 'remove' ? '#8B1A1A' : '#F5F5F5', border: '1px solid ' + (tokenAction === 'remove' ? '#8B1A1A' : '#D4CFC9'), borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontWeight: '500' }}>Remove</button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Tokens & placement</label>
              {[
                { key: 'cta_url', label: '{{cta_url}}', sub: 'booking or meeting link' },
                { key: 'landing_page', label: '{{landing_page}}', sub: 'product, service, or case study page' },
                { key: 'attachment', label: '{{attachment}}', sub: 'document, deck, brochure' }
              ].map(row => (
                <div key={row.key} style={{ border: '1px solid ' + (tokenRows[row.key].enabled ? '#553C9A' : '#E8E4E0'), borderRadius: '8px', padding: '0.7rem 0.85rem', marginBottom: '0.5rem', background: tokenRows[row.key].enabled ? '#FAF8FF' : '#FAFAF8' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={tokenRows[row.key].enabled}
                      onChange={e => setTokenRows(prev => ({ ...prev, [row.key]: { ...prev[row.key], enabled: e.target.checked } }))}
                      style={{ cursor: 'pointer' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.85rem', color: '#1A1A1A', fontWeight: '500', fontFamily: 'monospace' }}>{row.label}</div>
                      <div style={{ fontSize: '0.72rem', color: '#888' }}>{row.sub}</div>
                    </div>
                  </label>
                  {tokenRows[row.key].enabled && tokenAction === 'insert' && (
                    <div style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                      <label style={{ display: 'block', fontSize: '0.68rem', color: '#888', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Placement</label>
                      <select value={tokenRows[row.key].strategy}
                        onChange={e => setTokenRows(prev => ({ ...prev, [row.key]: { ...prev[row.key], strategy: e.target.value } }))}
                        style={{ width: '100%', fontSize: '0.8rem', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.5rem', backgroundColor: '#fff', outline: 'none' }}>
                        <option value="end_sales">End of every sales-step email</option>
                        <option value="end_all_emails">End of every email (sales + observation)</option>
                        <option value="after_meeting_ask">After the meeting ask sentence (sales steps only)</option>
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {tokenResult && (
              <div style={{ background: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '6px', padding: '0.6rem 0.85rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#22543D' }}>
                <div style={{ fontWeight: '600', marginBottom: '0.3rem' }}>Result · {tokenResult.requested} sequences targeted</div>
                {tokenResult.perToken.map(r => (
                  <div key={r.token} style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{' + r.token + '}}'} — {r.modified} updated</div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setTokenModalOpen(false)} disabled={tokenRunning}
                style={{ fontSize: '0.82rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: 'pointer' }}>
                Close
              </button>
              <button onClick={runBulkToken} disabled={tokenRunning}
                style={{ fontSize: '0.82rem', color: '#fff', background: tokenAction === 'insert' ? '#553C9A' : '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: 'pointer', fontWeight: '500', opacity: tokenRunning ? 0.6 : 1 }}>
                {tokenRunning ? 'Running...' : tokenAction === 'insert' ? 'Insert across ' + selectedIds.size + ' sequences' : 'Remove across ' + selectedIds.size + ' sequences'}
              </button>
            </div>
          </div>
        </div>
      )}

      {liTokenModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !liTokenRunning && setLiTokenModalOpen(false)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '520px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem', color: '#1A1A1A' }}>LinkedIn tokens — bulk action</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#888' }}>{selectedIds.size} sequences selected. Tokens are inserted into LinkedIn messages only. Email sequences are not touched by this action.</p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Action</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setLiTokenAction('insert')} style={{ flex: 1, fontSize: '0.8rem', color: liTokenAction === 'insert' ? '#fff' : '#1A1A1A', background: liTokenAction === 'insert' ? '#0A66C2' : '#F5F5F5', border: '1px solid ' + (liTokenAction === 'insert' ? '#0A66C2' : '#D4CFC9'), borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontWeight: '500' }}>Insert</button>
                <button onClick={() => setLiTokenAction('remove')} style={{ flex: 1, fontSize: '0.8rem', color: liTokenAction === 'remove' ? '#fff' : '#1A1A1A', background: liTokenAction === 'remove' ? '#8B1A1A' : '#F5F5F5', border: '1px solid ' + (liTokenAction === 'remove' ? '#8B1A1A' : '#D4CFC9'), borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontWeight: '500' }}>Remove</button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Tokens & placement</label>
              {[
                { key: 'cta_url', label: '{{cta_url}}', sub: 'booking or meeting link' },
                { key: 'landing_page', label: '{{landing_page}}', sub: 'product, service, or case study page' },
                { key: 'attachment', label: '{{attachment}}', sub: 'document, deck, brochure' }
              ].map(row => (
                <div key={row.key} style={{ border: '1px solid ' + (liTokenRows[row.key].enabled ? '#0A66C2' : '#E8E4E0'), borderRadius: '8px', padding: '0.7rem 0.85rem', marginBottom: '0.5rem', background: liTokenRows[row.key].enabled ? '#F0F7FF' : '#FAFAF8' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={liTokenRows[row.key].enabled}
                      onChange={e => setLiTokenRows(prev => ({ ...prev, [row.key]: { ...prev[row.key], enabled: e.target.checked } }))}
                      style={{ cursor: 'pointer' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.85rem', color: '#1A1A1A', fontWeight: '500', fontFamily: 'monospace' }}>{row.label}</div>
                      <div style={{ fontSize: '0.72rem', color: '#888' }}>{row.sub}</div>
                    </div>
                  </label>
                  {liTokenRows[row.key].enabled && liTokenAction === 'insert' && (
                    <div style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                      <label style={{ display: 'block', fontSize: '0.68rem', color: '#888', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Placement</label>
                      <select value={liTokenRows[row.key].strategy}
                        onChange={e => setLiTokenRows(prev => ({ ...prev, [row.key]: { ...prev[row.key], strategy: e.target.value } }))}
                        style={{ width: '100%', fontSize: '0.8rem', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.5rem', backgroundColor: '#fff', outline: 'none' }}>
                        <option value="end_sales_li">End of every sales-step LinkedIn message</option>
                        <option value="end_all_li">End of every LinkedIn message</option>
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {liTokenResult && (
              <div style={{ background: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '6px', padding: '0.6rem 0.85rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#22543D' }}>
                <div style={{ fontWeight: '600', marginBottom: '0.3rem' }}>Result · {liTokenResult.requested} sequences targeted</div>
                {liTokenResult.perToken.map(r => (
                  <div key={r.token} style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{'{{' + r.token + '}}'} — {r.modified} updated</div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setLiTokenModalOpen(false)} disabled={liTokenRunning}
                style={{ fontSize: '0.82rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: 'pointer' }}>
                Close
              </button>
              <button onClick={runBulkLiToken} disabled={liTokenRunning}
                style={{ fontSize: '0.82rem', color: '#fff', background: liTokenAction === 'insert' ? '#0A66C2' : '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.45rem 0.85rem', cursor: 'pointer', fontWeight: '500', opacity: liTokenRunning ? 0.6 : 1 }}>
                {liTokenRunning ? 'Running...' : liTokenAction === 'insert' ? 'Insert across ' + selectedIds.size + ' sequences' : 'Remove across ' + selectedIds.size + ' sequences'}
              </button>
            </div>
          </div>
        </div>
      )}

      {addToBatchOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeAddToBatch}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '480px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>Add to batch</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#888' }}>{selectedIds.size} contacts selected · {addToBatchChannel === 'linkedin' ? linkedinApproved.length : emailApproved.length} eligible</p>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.4rem', color: '#4A5568' }}>Channel</div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => changeBatchChannel('linkedin')}
                  style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid ' + (addToBatchChannel === 'linkedin' ? '#0A66C2' : '#DDD'), background: addToBatchChannel === 'linkedin' ? '#EBF8FF' : '#FFF', color: addToBatchChannel === 'linkedin' ? '#0A66C2' : '#666', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>
                  LinkedIn ({linkedinApproved.length})
                </button>
                <button onClick={() => changeBatchChannel('email')}
                  style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid ' + (addToBatchChannel === 'email' ? '#1A56A0' : '#DDD'), background: addToBatchChannel === 'email' ? '#EBF8FF' : '#FFF', color: addToBatchChannel === 'email' ? '#1A56A0' : '#666', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}>
                  Email ({emailApproved.length})
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.4rem', color: '#4A5568' }}>Target batch</div>
              {!creatingNewBatch ? (
                <>
                  <select value={selectedBatchId} onChange={e => setSelectedBatchId(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #DDD', fontSize: '0.85rem', background: '#FFF' }}>
                    <option value="">Choose a batch…</option>
                    {availableBatches.map(b => (
                      <option key={b.id} value={b.id}>{b.name} ({b.contact_count} contacts)</option>
                    ))}
                  </select>
                  <button onClick={() => setCreatingNewBatch(true)} style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#1A56A0', fontSize: '0.78rem', cursor: 'pointer', padding: 0 }}>+ Create new batch</button>
                </>
              ) : (
                <>
                  <input type="text" value={newBatchName} onChange={e => setNewBatchName(e.target.value)} placeholder="New batch name" autoFocus
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #DDD', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                  {addToBatchChannel === 'email' && (
                    <select value={newBatchDestination} onChange={e => setNewBatchDestination(e.target.value)}
                      style={{ width: '100%', marginTop: '0.5rem', padding: '0.5rem', borderRadius: '6px', border: '1px solid #DDD', fontSize: '0.85rem', background: '#FFF' }}>
                      <option value="pipedrive_csv">Pipedrive (CSV)</option>
                      <option value="hubspot_csv">HubSpot (CSV)</option>
                      <option value="bigin_csv">Zoho Bigin (CSV)</option>
                      <option value="zoho_crm_csv">Zoho CRM (CSV)</option>
                      <option value="salesforce_csv">Salesforce (CSV)</option>
                    </select>
                  )}
                  <button onClick={() => setCreatingNewBatch(false)} style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#888', fontSize: '0.78rem', cursor: 'pointer', padding: 0 }}>← Pick existing</button>
                </>
              )}
            </div>

            <p style={{ fontSize: '0.72rem', color: '#888', margin: '0 0 1rem' }}>
              Only contacts with an approved {addToBatchChannel} sequence will be added. Others will be skipped.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={closeAddToBatch} disabled={batchSubmitting}
                style={{ background: '#FFF', color: '#333', border: '1px solid #DDD', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitAddToBatch} disabled={batchSubmitting}
                style={{ background: '#1A56A0', color: '#FFF', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: batchSubmitting ? 'not-allowed' : 'pointer', opacity: batchSubmitting ? 0.6 : 1 }}>
                {batchSubmitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ContactPanel overlay */}
      {openContactId && (
        <ContactPanel
          contactId={openContactId}
          clientId={id}
          onClose={() => { setOpenContactId(null); refreshSeqs(); }}
          onSequence={async (contactId, clientId) => {
            const toastId = toast.loading('Generating sequence — this can take up to 60 seconds...', { duration: 90000 });
            try {
              const res = await creditAwareFetch(API_BASE + '/contacts/' + contactId + '/generate-sequence', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ client_id: clientId || id, prompt: '' })
              });
             if (!res) return;
              if (!res) { toast.dismiss(toastId); return; }
              if (res.status === 499) { toast('Sequence generation stopped', { id: toastId, icon: '⏹️' }); return; }
              if (!res.ok) { toast.error('Sequence generation failed', { id: toastId }); return; }
              const data = await res.json();
              await fetch(API_BASE + '/contacts/' + contactId + '/sequences', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ client_id: clientId || id, email_sequence: data.email_sequence, linkedin_sequence: data.linkedin_sequence, call_sequence: data.call_sequence })
              });
              toast.success('Sequence generated', { id: toastId, duration: 4000 });
              await refreshSeqs();
              try { window.dispatchEvent(new CustomEvent('ce:sequenceGenerated', { detail: { contactId, clientId } })); } catch {}
            } catch (e) {
              toast.error('Sequence generation failed', { id: toastId });
              console.error(e);
            }
          }}
          onDelete={() => { setOpenContactId(null); fetchAll(); }}
          userEmail={user?.primaryEmailAddress?.emailAddress}
        />
      )}

      <ConfirmActionModal
        open={confirmGenerateOpen}
        title={'Generate ' + pendingGenerateCount + ' sequence' + (pendingGenerateCount === 1 ? '' : 's') + '?'}
        body={'This will generate ' + pendingGenerateCount + ' AI sequence' + (pendingGenerateCount === 1 ? '' : 's') + '. This may take a minute and consumes credits.'}
        confirmLabel={'Generate ' + pendingGenerateCount}
        onConfirm={doGenerate}
        onCancel={() => setConfirmGenerateOpen(false)}
      />
    </div>
  );
}
